import Joi from "joi";
import { useEffect, useMemo, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getTaskRespects,
  getTaskStatus,
  getDepartments,
} from "../../../../../action/common/commonActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import { cloneDeep } from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import { TaskRespects } from "../../../../../models/dashboard/adminDashboard";
import { IEditTask, ITaskEditData } from "../../../../../models/master/task";
import { setTask } from "../../../../../store/slices/master/taskSlice";
import { updateTask } from "../../../../../action/masters/taskActionCreator";
import moment from "moment";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { IDropdownData } from "../../../../../models/ICommon";

const TaskEdit = (props: IEditTask) => {
  const dispatch = useDispatch();
  const {
    updateTask,
    getDepartments,
    departments,
    taskRespects,
    getTaskRespects,
    getTaskStatus,
    taskStatus,
    getAllTasks,
    task,
    setShowEdit,
  } = props;

  // states

  const [data, setData] = useState<ITaskEditData>(cloneDeep(task));
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [allTaskStatus, setAllTaskStatus] = useState<IDropdownData[]>([]);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    setData(_data);
  };

  useEffect(() => {
    if (task) {
      let _task = cloneDeep(task);
      _task.startDate = moment(_task.startDate).format("YYYY-MM-DD");
      _task.endDate = moment(_task.endDate).format("YYYY-MM-DD");
      setData(_task);
    }
  }, [task]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllTasks();
      setShowEdit(false);
    }
  };

  const schema = Joi.object().keys({
    name: Joi.string().required().label("Name is required"),
    description: Joi.string().required().label("Description is required"),
    startDate: Joi.string().required().label("Start Date is required"),
    endDate: Joi.string().required().label("End Date is required"),
    taskRespect: Joi.number()
      .greater(0)
      .required()
      .label("Task Respect is required"),
    taskCurrentStatus: Joi.number()
      .greater(0)
      .required()
      .label("Task Status is required"),
    departmentId: Joi.when("taskRespect", {
      is: TaskRespects.Department,
      then: Joi.number().required().label("Department is required"),
      otherwise: Joi.number().allow(null),
    }),
  });

  const onUpdate = async () => {
    const validationErrors = validateFields(data, schema);
    if (validationErrors === undefined) {
      try {
        setLoading(true);
        await updateTask(data, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    getDepartments();
    getTaskRespects();
    getTaskStatus();
  }, []);

  useEffect(() => {
    if (taskStatus?.length > 0) {
      setAllTaskStatus(taskStatus);
    }
  }, [taskStatus]);

  useEffect(() => {
    return () => {
      dispatch(setTask(undefined));
    };
  }, []);

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    errors && delete errors[name];
    const updatedData = { ...data, [name]: value };

    // Validate dates
    if (name === "endDate" && value < updatedData.startDate) {
      setErrors({
        ...errors,
        endDate: "End date cannot be earlier than the start date.",
      });
    } else if (
      name === "startDate" &&
      updatedData.endDate &&
      updatedData.endDate < value
    ) {
      setErrors({ ...errors, endDate: "Please adjust the end date." });
      updatedData.endDate = "";
    }

    if (name === "startDate" && moment(value).isAfter(moment(), "day")) {
      updatedData.taskCurrentStatus = 0;
    }
    setData(updatedData);
  };

  const isStartDateInFuture = () => {
    return moment(data?.startDate).isAfter(moment(), "day");
  };

  const getFilteredTaskStatus = useMemo(() => {
    if (isStartDateInFuture()) {
      return allTaskStatus.filter(
        (status: any) =>
          status.name === "Open" ||
          status.name === "Hold"
      );
    }
    return allTaskStatus;
  }, [taskStatus, isStartDateInFuture])

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Edit Task
            </h1>
          </div>
          <div className="modal-body">
            <div className="row mt-3">
              <div className="col-md-12 form-group">
                <FloatingLabelTextInput
                  label={"Task Name"}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.name}
                  name={"name"}
                />
                {errors?.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <label className="profile_details_text">Task Description</label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <label className="profile_details_text">Task Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  className="form-control"
                  onChange={onChangeDate}
                  value={data.startDate}
                  min={new Date().toISOString().split("T")[0]}
                />
                {errors?.startDate && (
                  <div className="text-danger">{errors.startDate}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <label className="profile_details_text">Task End Date</label>
                <input
                  type="date"
                  name="endDate"
                  className="form-control"
                  onChange={onChangeDate}
                  value={data.endDate}
                  min={data.startDate}
                />
                {errors?.endDate && (
                  <div className="text-danger">{errors.endDate}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Task Current Status "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.taskCurrentStatus}
                  name={"taskCurrentStatus"}
                  optionsList={getFilteredTaskStatus || []}
                />
                {errors?.taskCurrentStatus && (
                  <div className="text-danger">{errors.taskCurrentStatus}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Task with respect to"}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.taskRespect}
                  name={"taskRespect"}
                  optionsList={sortAlphabetically(taskRespects)}
                />
                {errors?.taskRespect && (
                  <div className="text-danger">{errors.taskRespect}</div>
                )}
              </div>
              {data.taskRespect === TaskRespects.Department && (
                <div className="col-md-12 form-group">
                  <FloatingLabelDropDown
                    label={"Department"}
                    onChange={onChange}
                    isMandatory={true}
                    value={data.departmentId}
                    name={"departmentId"}
                    optionsList={sortAlphabetically(departments)}
                  />
                  {errors?.departmentId && (
                    <div className="text-danger">{errors.departmentId}</div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {isLoading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateTask: (data: ITaskEditData, id: number, callBack: Function) =>
    updateTask(data, id, callBack),
  getTaskRespects: () => getTaskRespects(),
  getTaskStatus: () => getTaskStatus(),
  getDepartments: () => getDepartments(),
};

function mapStateToProps(state: any) {
  return {
    departments: state.common.departments,
    taskStatus: state.common.taskStatus,
    taskRespects: state.common.taskRespects,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskEdit);
